import * as React from "react";
import { STEPPER_THEME, type StepperTheme } from "./Stepper.theme";

type ClassName = string;
type IsStepFn = (value: boolean) => void;

function cx(...parts: Array<string | undefined | null | false>) {
  return parts.filter(Boolean).join(" ");
}

function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (value: T | null) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") ref(value);
      else if (ref) (ref as any).current = value;
    });
  };
}

function splitBackgroundClass(className: string): { bgClass?: string; rest: string } {
  const parts = className.split(/\s+/).filter(Boolean);
  let bgClass: string | undefined;

  const restParts = parts.filter((part) => {
    const isBg = part.startsWith("bg-") || part.startsWith("bg[") || part.startsWith("bg-opacity-");
    if (isBg) bgClass = part;
    return !isBg;
  });

  return { bgClass, rest: restParts.join(" ") };
}

function mergeTheme(base: StepperTheme, override?: Partial<StepperTheme>): StepperTheme {
  return override ? { ...base, ...override } : base;
}

// Step
export interface StepProps extends React.ComponentPropsWithoutRef<"div"> {
  className?: ClassName;
  activeClassName?: ClassName;
  completedClassName?: ClassName;
  children?: React.ReactNode;

  label?: React.ReactNode;
  labelClassName?: ClassName;
  activeLabelClassName?: ClassName;
  completedLabelClassName?: ClassName;

  labelPosition?: "bottom" | "right";

  // "absolute" keeps labels absolutely positioned, doesn't affect layout width
  // "flow" makes the labels take up space so horizontal intrinsic mode can size to full label width
  labelLayout?: "absolute" | "flow";

  // Optional: override the base dot styling.
  // Stepper injects this automatically when it has a theme.
  dotBaseClassName?: ClassName;

  ["data-stepper-step"]?: string;
}

function splitInteractiveClasses(className?: string) {
  if (!className) return {};
  const parts = className.split(/\s+/).filter(Boolean);
  const wrapperParts = parts.filter((p) => p === "group" || p.startsWith("cursor-"));
  const dotParts = parts.filter((p) => p !== "group" && !p.startsWith("cursor-"));

  return {
    wrapperClassName: wrapperParts.join(" "),
    dotClassName: dotParts.join(" "),
  };
}

export const Step = React.forwardRef<HTMLDivElement, StepProps>(
  (
    {
      className,
      children,
      label,
      labelClassName,
      activeClassName,
      completedClassName,
      activeLabelClassName,
      completedLabelClassName,
      dotBaseClassName,
      labelPosition = "bottom",
      labelLayout = "absolute",
      ...rest
    },
    ref,
  ) => {
    // When Step is used standalone (not inside Stepper), keep the original default:
    // structural + full base color (bg + text)
    const dotBase = dotBaseClassName ?? cx(
      STEPPER_THEME.dotBaseStructuralClassName,
      STEPPER_THEME.dotBaseColorClassName,
    );

    // Flow layout: label participates in layout width
    // Align label start to the dot's left edge via items-start
    if (labelLayout === "flow" && labelPosition === "bottom") {
      const { wrapperClassName, dotClassName } = splitInteractiveClasses(className);
      return (
        <div
          {...rest}
          ref={ref}
          className={cx("relative z-10 inline-flex flex-col items-start", wrapperClassName)}
        >
          <div data-stepper-dot="true" className={cx(dotBase, dotClassName)}>
            {children}
          </div>
          {label != null && (
            <span className={cx(STEPPER_THEME.labelBaseFlowClassName, labelClassName)}>
              {label}
            </span>
          )}
        </div>
      );
    }

    const labelPosClasses = labelPosition === "right"
      ? STEPPER_THEME.labelPosRightClassName
      : STEPPER_THEME.labelPosBottomClassName;

    // Mark this element as the dot for geometry calculations
    return (
      <div {...rest} ref={ref} data-stepper-dot="true" className={cx(dotBase, className)}>
        {children}
        {label != null && (
          <span className={cx(STEPPER_THEME.labelBaseAbsoluteClassName, labelPosClasses, labelClassName)}>
            {label}
          </span>
        )}
      </div>
    );
  },
);

Step.displayName = "Step";

// Stepper
type StepperOrientation = "horizontal" | "vertical";
type HorizontalMode = "stretch" | "intrinsic";
type VerticalMode = "gap" | "fill";

export interface StepperProps extends React.ComponentPropsWithoutRef<"div"> {
  activeStep?: number;
  isFirstStep?: IsStepFn;
  isLastStep?: IsStepFn;
  orientation?: StepperOrientation;

  // "stretch" (default): existing behavior (full width, evenly distributed)
  // "intrinsic": width grows to fit full labels (best for mobile scroller)
  horizontalMode?: HorizontalMode;

  // Vertical layout:
  // - "gap" (default): current behavior, fixed gaps (gap-10).
  // - "fill": steps distributed to fill the parent's height (requires the Stepper to have a height, e.g. parent h-full).
  verticalMode?: VerticalMode;

  className?: ClassName;
  lineClassName?: ClassName;
  activeLineClassName?: ClassName;
  children: React.ReactNode;

  // Optional theme override. Defaults to STEPPER_THEME (matches current visuals).
  theme?: Partial<StepperTheme>;
}

const StepperImpl = React.forwardRef<HTMLDivElement, StepperProps>(
  (
    {
      activeStep = 0,
      isFirstStep,
      isLastStep,
      orientation = "horizontal",
      horizontalMode = "stretch",
      verticalMode = "gap",
      className,
      lineClassName,
      activeLineClassName,
      children,
      style,
      theme,
      ...rest
    },
    ref,
  ) => {
    const mergedTheme = React.useMemo(() => mergeTheme(STEPPER_THEME, theme), [theme]);
    const containerRef = React.useRef<HTMLDivElement | null>(null);

    const handleRef = React.useMemo(() => mergeRefs(ref, containerRef), [ref, containerRef]);

    const childrenArray = React.useMemo(
      () => React.Children.toArray(children) as React.ReactElement<StepProps>[],
      [children],
    );

    const stepsCount = childrenArray.length;
    const isVertical = orientation === "vertical";
    const isHorizontalIntrinsic = !isVertical && horizontalMode === "intrinsic";

    const [geo, setGeo] = React.useState({
      widthPerStep: 0,
      hDotX: [] as number[],
      hLineY: 0,
      vDotY: [] as number[],
      vLineX: 0,
    });

    const recalc = React.useCallback(() => {
      const el = containerRef.current;
      if (!el || stepsCount <= 1) {
        // Reset if invalid
        setGeo({ widthPerStep: 0, hDotX: [], hLineY: 0, vDotY: [], vLineX: 0 });
        return;
      }

      const containerRect = el.getBoundingClientRect();
      const dotEls = Array.from(el.querySelectorAll<HTMLElement>('[data-stepper-dot="true"]'));

      if (dotEls.length !== stepsCount) return;

      if (isVertical) {
        const vDotY = dotEls.map((node) => {
          const r = node.getBoundingClientRect();
          return r.top + r.height / 2 - containerRect.top;
        });
        const firstRect = dotEls[0].getBoundingClientRect();
        const vLineX = firstRect.left + firstRect.width / 2 - containerRect.left;

        setGeo({ widthPerStep: 0, hDotX: [], hLineY: 0, vDotY, vLineX });
      } else if (isHorizontalIntrinsic) {
        const hDotX = dotEls.map((node) => {
          const r = node.getBoundingClientRect();
          return r.left + r.width / 2 - containerRect.left;
        });
        const firstRect = dotEls[0].getBoundingClientRect();
        const hLineY = firstRect.top + firstRect.height / 2 - containerRect.top;

        setGeo({ widthPerStep: 0, hDotX, hLineY, vDotY: [], vLineX: 0 });
      } else {
        // Horizontal Stretch
        setGeo(prev => ({
          ...prev,
          widthPerStep: containerRect.width / (stepsCount - 1),
          // Clean up others just in case
          hDotX: [], vDotY: []
        }));
      }
    }, [stepsCount, isVertical, isHorizontalIntrinsic]);

    React.useLayoutEffect(() => {
      recalc();
    }, [recalc]);

    React.useEffect(() => {
      const el = containerRef.current;
      if (!el) return;

      const ro = new ResizeObserver(recalc);
      ro.observe(el);
      return () => ro.disconnect();
    }, [recalc]);

    const isFirstStepValue = activeStep === 0;
    const isLastStepValue = stepsCount > 0 ? activeStep === stepsCount - 1 : true;

    React.useEffect(() => {
      isFirstStep?.(isFirstStepValue);
      isLastStep?.(isLastStepValue);
    }, [isFirstStep, isLastStep, isFirstStepValue, isLastStepValue]);

    const clampedActive = stepsCount > 0 ? Math.max(0, Math.min(activeStep, stepsCount - 1)) : 0;

    // Derived Geometry
    const activeWidth = geo.widthPerStep * clampedActive;

    // Intrinsic horizontal
    const hStart = geo.hDotX[0] ?? 0;
    const hEnd = geo.hDotX[geo.hDotX.length - 1] ?? 0;
    const hActiveX = geo.hDotX[clampedActive] ?? hStart;
    const hTotalW = Math.max(0, hEnd - hStart);
    const hActiveW = Math.max(0, hActiveX - hStart);

    // Vertical
    const vStart = geo.vDotY[0] ?? 0;
    const vEnd = geo.vDotY[geo.vDotY.length - 1] ?? 0;
    const vActiveY = geo.vDotY[clampedActive] ?? vStart;
    const activeHeight = Math.max(0, vActiveY - vStart);
    const totalVerticalHeight = Math.max(0, vEnd - vStart);

    const isVerticalFill = isVertical && verticalMode === "fill";

    const stepperClasses = cx(
      isVertical
        ? isVerticalFill
          ? "w-full h-full relative flex flex-col items-start justify-between stepper-vertical"
          : "w-full relative flex flex-col items-start gap-10 stepper-vertical"
        : isHorizontalIntrinsic
          ? "relative inline-flex items-center gap-10 stepper-horizontal"
          : "w-full relative flex items-center justify-between stepper-horizontal",
      className,
    );

    // Control top/left/width precisely via inline style
    const baseLine = cx(
      "absolute",
      isVertical
        ? cx("-translate-x-2/4", mergedTheme.lineThicknessVerticalClassName)
        : isHorizontalIntrinsic
          ? mergedTheme.lineThicknessHorizontalClassName
          : cx("left-0 top-2/4 w-full -translate-y-2/4", mergedTheme.lineThicknessHorizontalClassName)
    );

    const lineStyle: React.CSSProperties | undefined = isVertical
      ? { top: vStart, height: totalVerticalHeight, left: geo.vLineX }
      : isHorizontalIntrinsic
        ? { top: geo.hLineY, left: hStart, width: hTotalW, transform: "translateY(-50%)" }
        : undefined;

    const activeLineStyle: React.CSSProperties | undefined = isVertical
      ? { top: vStart, height: activeHeight, left: geo.vLineX }
      : isHorizontalIntrinsic
        ? { top: geo.hLineY, left: hStart, width: hActiveW, transform: "translateY(-50%)" }
        : { width: activeWidth };

    const mergedStyle = React.useMemo(() => ({
      ...style,
      "--stepper-ring-gap": mergedTheme.ringGapColor,
      "--stepper-ring-color": mergedTheme.ringColor,
      "--stepper-ring-gap-size": mergedTheme.ringGapSize,
      "--stepper-ring-outline-size": mergedTheme.ringOutlineSize,
    } as React.CSSProperties), [style, mergedTheme.ringGapColor, mergedTheme.ringColor, mergedTheme.ringGapSize, mergedTheme.ringOutlineSize]);

    const { baseDotInjected, futureBg, activeBg, completedBg } = React.useMemo(() => {
      const baseSplit = splitBackgroundClass(mergedTheme.dotBaseColorClassName);
      const activeSplit = splitBackgroundClass(mergedTheme.activeDotColorClassName);
      const completedSplit = splitBackgroundClass(mergedTheme.completedDotColorClassName);

      const future = baseSplit.bgClass;
      return {
        baseDotInjected: cx(mergedTheme.dotBaseStructuralClassName, baseSplit.rest),
        futureBg: future,
        activeBg: activeSplit.bgClass ?? future,
        completedBg: completedSplit.bgClass ?? future,
      };
    }, [mergedTheme.dotBaseColorClassName, mergedTheme.activeDotColorClassName, mergedTheme.completedDotColorClassName, mergedTheme.dotBaseStructuralClassName]);

    return (
      <div
        {...rest}
        ref={handleRef}
        className={stepperClasses}
        style={mergedStyle}
      >
        <div className={cx(baseLine, mergedTheme.lineInactiveColorClassName, lineClassName)} style={lineStyle} />
        <div
          className={cx(baseLine, mergedTheme.lineActiveColorClassName, mergedTheme.lineActiveMotionClassName, activeLineClassName)}
          style={activeLineStyle}
        />

        {childrenArray.map((child, index) => {
          const isActive = index === activeStep;
          const isCompleted = index < activeStep;
          const bgForState = isActive ? activeBg : isCompleted ? completedBg : futureBg;

          return React.cloneElement(child, {
            key: child.key ?? index,
            ...child.props,
            "data-stepper-step": "true",

            // Inject a base dot class WITHOUT bg-* so state bg never conflicts
            dotBaseClassName: child.props.dotBaseClassName ?? baseDotInjected,
            className: cx(
              child.props.className,

              // Apply exactly one background class for this state (if provided)
              bgForState,
              isActive
                ? cx(mergedTheme.activeStepStructuralClassName, child.props.activeClassName)
                : isCompleted
                  ? cx(mergedTheme.completedStepStructuralClassName, child.props.completedClassName)
                  : "",
            ),
            labelClassName: cx(
              child.props.labelClassName,
              isActive ? child.props.activeLabelClassName : isCompleted ? child.props.completedLabelClassName : undefined
            ),
            labelPosition: child.props.labelPosition ?? (isVertical ? "right" : "bottom"),
            labelLayout: child.props.labelLayout ?? (isHorizontalIntrinsic ? "flow" : "absolute"),
          });
        })}
      </div>
    );
  },
);

StepperImpl.displayName = "Stepper";

export const Stepper = Object.assign(StepperImpl, { Step });
